import { useCallback } from "react";
import axios from "axios";
import { useAlerts } from "common";
import { AUTOCOMPLETE_DEBOUNCE_DELAY } from "app/shared/constants";
import { getStoreValue, setStoreValue } from "app/shared/utils";
import { useForms } from "./formsService";

let timeoutId = [];

export const useApi = (formik, globalValueObj, formFlatMap, setRender) => {
  const { clearAlert, setAlert } = useAlerts();
  const { setOptionsLoading } = useForms();

  const callApi = useCallback(
    async (url, id) => {
      try {
        if (!url.includes("{}")) {
          clearAlert();
          const URL = url.replace(/['"]+/g, "");
          const response = await axios.get(URL);
          const data = (response.data?.values ?? response.data) || [];
          globalValueObj[id] = data;
          formik.values[id] = data;
          formFlatMap[id] = data;
          setRender((value) => value + 1);
        }
      } catch (error) {
        console.log(id, error);
        setAlert("error", error.message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearAlert, setAlert],
  );

  const callApiSync = useCallback(
    async (url) => {
      try {
        if (!url.includes("{}")) {
          clearAlert();
          const URL = url.replace(/['"]+/g, "");
          return axios.get(URL);
        }
      } catch (error) {
        console.log(error);
        setAlert("error", error.message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearAlert, setAlert],
  );

  /**
   * PURPOSE:
   *  Conditionally clears a form field's value across multiple data structures.
   *  Prevents clearing fields on the initial API call (first render) but clears them
   *  on subsequent user-triggered calls. This ensures data persistence on initial load
   *  while allowing field reset on user interactions.
   *
   * @param {string} id - A valid field identifier that exists in globalValueObj, formik.values, and formFlatMap
   * @param {boolean} [resetInitialCall=false] - Forces the call to behave like an initial call when true,
   *                                              preventing field clearing even on subsequent calls.
   *                                              Useful for preserving field data during secondary API calls
   *                                              (e.g., dependent field updates or re-fetching data without
   *                                              resetting user input).
   * BEHAVIOR:
   * - When initial call (resetInitialCall === true OR stored initialCall === true):
   *   - Store is updated via setStoreValue("clearField", { initialCall: false })
   *   - Field values remain unchanged
   * - When subsequent call (resetInitialCall === false AND stored initialCall === false):
   *   - globalValueObj[id], formik.values[id], and formFlatMap[id] are set to ""
   *   - Store is not modified
   */
  const clearField = (id, resetInitialCall = false) => {
    const initialCall = resetInitialCall
      ? true
      : getStoreValue("clearField").initialCall;
    if (initialCall) {
      setStoreValue("clearField", { initialCall: false });
    } else {
      globalValueObj[id] = "";
      formik.values[id] = "";
      formFlatMap[id] = "";
    }
  };

  // callApiDebounce - would be used for the calls where user needs to get data dynamically
  // on the basis of searched keyword
  // This function is adding a debounce in calling the API in order to prevent multiple calls
  const callApiDebounce = useCallback(
    async (url, id) => {
      clearTimeout(timeoutId[id]);
      setOptionsLoading(true);
      timeoutId[id] = setTimeout(async () => {
        try {
          if (!url.includes("{}")) {
            const URL = url.replace(/['"]+/g, "");
            const response = await axios.get(URL);
            const data = (response.data?.values ?? response.data) || [];
            globalValueObj[id] = data;
            formik.values[id] = data;
            formFlatMap[id] = data;
            setRender((value) => value + 1);
          }
        } catch (error) {
          console.log(id, error);
          clearAlert();
          setAlert("error", error.message);
        } finally {
          setOptionsLoading(false);
        }
      }, AUTOCOMPLETE_DEBOUNCE_DELAY);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearAlert, setAlert],
  );

  return { callApi, callApiDebounce, callApiSync, clearField };
};
